import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, AlertTriangle, X as XIcon } from "lucide-react";
import { useAuth } from "../Context/AuthContext";

const API_BASE = "https://apisellhub.com/api";

const DeletePlanDialog = ({ open, plan, onClose, onSuccess }) => {
    const { getToken } = useAuth();
    const [deleting, setDeleting] = useState(false);

    const handleDelete = async () => {
        if (!plan) return;
        setDeleting(true);
        try {
            const token = getToken();
            const response = await axios.delete(
                `${API_BASE}/super-admin/pricing-plans/${plan.id}/delete`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data?.status !== false) {
                toast.success("Plan deleted");
                onSuccess?.();
            } else {
                toast.error(response.data?.message ?? "Failed to delete plan");
            }
        } catch (err) {
            console.error("Delete plan error:", err);
            toast.error(err.response?.data?.message ?? "Failed to delete plan");
        } finally {
            setDeleting(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-black/60"
                onClick={() => !deleting && onClose?.()}
            />

            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-red-600" />
                        Delete Pricing Plan
                    </h2>
                    <button
                        onClick={onClose}
                        disabled={deleting}
                        className="p-1 rounded-lg hover:bg-gray-100 text-gray-500"
                    >
                        <XIcon className="h-5 w-5" />
                    </button>
                </div>

                <div className="px-6 py-5">
                    <p className="text-sm text-gray-900">
                        Are you sure you want to delete <strong>{plan?.name}</strong>?
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                        Sellers currently on this plan may be affected. This action cannot be undone.
                    </p>
                </div>

                <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <button
                        onClick={onClose}
                        disabled={deleting}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleDelete}
                        disabled={deleting}
                        className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                    >
                        {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Delete Plan
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DeletePlanDialog;
